/**
 * hydrateSelectedTract.ts — authoritative geoid re-hydration for the ranked-list path (T-233)
 *
 * Root cause (per the T-233 re-trace): a ranked-list row carries the raw `map.querySourceFeatures`
 * feature verbatim; when that snapshot is incomplete (a mid-reload query can leave only the
 * choropleth's own `lvi` + `dominant_type`), `featurePropsToTract` yields a tract with a valid
 * HEADLINE but every metric undefined/null. Because the list path issued no authoritative fetch
 * and cleared the only self-heal, the panel dashed and stayed dashed until an unrelated map-click
 * overwrote it. The map-click and search paths never had this — they resolve tract detail from the
 * server (`/api/tracts/at`).
 *
 * This makes the ranked-list selection authoritative too: keep the optimistic (instant) tract for
 * the headline + pin, then fetch the full tract by geoid and apply it. The policy — apply only if
 * the selection is still current (guards the rapid-clicking / superseded-by-map-click race), and
 * keep the optimistic tract on failure (degraded-but-present beats clearing) — lives here so it is
 * unit-testable in isolation from the MapShell monolith. It does NOT touch site-score semantics
 * (the T-222 "no re-score" rule is about /api/score/site, not tract detail).
 */
import type { CensusTract } from './tractModel'

export interface HydrateSelectedTractDeps {
  /** Authoritative server fetch for the full tract (GET /api/tracts/:geoid). */
  fetchTract: (geoid: string) => Promise<CensusTract>
  /** True iff `geoid` is still the selected tract when the fetch resolves (staleness guard). */
  isStillSelected: (geoid: string) => boolean
  /** Commit the hydrated tract to app state (setActiveTract). */
  apply: (tract: CensusTract) => void
}

/**
 * Re-hydrate a ranked-list selection with the full server tract, guarded against staleness and
 * failure. Never throws and never clears the selection.
 */
export async function hydrateSelectedTract(
  geoid: string,
  deps: HydrateSelectedTractDeps,
): Promise<void> {
  let full: CensusTract
  try {
    full = await deps.fetchTract(geoid)
  } catch {
    // Fetch failed (network / 404 / 5xx) — keep the optimistic tract in place. A degraded-but-
    // present headline beats clearing the panel or leaving it dashed.
    return
  }
  // Apply only if this tract is STILL the current selection. A newer ranked-row click, a map-click,
  // or a search may have superseded it while the request was in flight — applying now would clobber
  // the newer selection with stale detail.
  if (deps.isStillSelected(full.tractId)) {
    deps.apply(full)
  }
}
